<template>
  <v-not-found v-if="notFound" />
  <div class="route-item-listing" v-else>
    <v-header info-toggle :item-detail="false" :breadcrumb="breadcrumb">
      <template slot="title">
        <button
          :class="currentBookmark ? 'active' : null"
          :disabled="currentBookmark"
          class="bookmark"
          @click="bookmarkModal = true"
        >
          <i class="material-icons">
            {{ currentBookmark ? "bookmark" : "bookmark_border" }}
          </i>
        </button>
        <div v-if="currentBookmark" class="bookmark-name no-wrap">
          ({{ currentBookmark.title }})
        </div>
      </template>
      <v-search-filter
        v-show="selection && selection.length === 0 && !emptyCollection"
        :filters="filters"
        :search-query="searchQuery"
        :field-names="fieldNames"
        :placeholder="resultCopy"
        @filter="updatePreferences('filters', $event)"
        @search="updatePreferences('search_query', $event)"
        @clear-filters="clearFilters"
      />
      <template slot="buttons">
        <v-header-button
          v-if="editButton && !activity"
          key="edit"
          icon="mode_edit"
          color="warning"
          :disabled="!editButtonEnabled"
          :label="$t('batch')"
          :to="batchURL"
        />
        <v-header-button
          v-if="deleteButton && !activity"
          key="delete"
          icon="delete_outline"
          color="danger"
          :disabled="!deleteButtonEnabled"
          :label="$t('delete')"
          @click="confirmRemove = true"
        />
        <v-header-button
          v-if="addButton && !activity"
          icon="add"
          key="add"
          color="action"
          :label="$t('new')"
          :to="`/collections/${collection}/+`"
        />
      </template>
    </v-header>

    <v-items
      v-if="preferences"
      ref="listing"
      :collection="collection"
      :filters="filters"
      :search-query="searchQuery"
      :view-query="viewQuery"
      :view-type="viewType"
      :view-options="viewOptions"
      :selection="!activity ? selection : null"
      links
      @fetch="meta = $event"
      @options="setViewOptions"
      @select="selection = $event"
      @query="setViewQuery"
    />

    <v-info-sidebar v-if="preferences">
      <template slot="system">
        <div>
          <button class="csv" @click="scaricaCSV()">Scarica CSV</button>
          <v-select
            id="layout"
            :options="layoutNames"
            :value="viewType"
            name="layout"
            @input="updatePreferences('view_type', $event)"
          />
        </div>
      </template>
      <v-ext-layout-options
        :key="`${collection}-${viewType}`"
        :type="viewType"
        :collection="collection"
        :fields="$lodash.keyBy(fields, 'field')"
        :view-options="viewOptions"
        :view-query="viewQuery"
        :selection="selection"
        link="__link__"
        @query="setViewQuery"
        @options="setViewOptions"
      />
    </v-info-sidebar>

    <portal to="modal" v-if="confirmRemove">
      <v-confirm
        :message="
          $tc('batch_delete_confirm', selection.length, {
            count: selection.length
          })
        "
        color="danger"
        :confirm-text="$t('delete')"
        @cancel="confirmRemove = false"
        @confirm="remove"
      />
    </portal>

    <portal to="modal" v-if="bookmarkModal">
      <v-prompt
        :message="$t('name_bookmark')"
        v-model="bookmarkTitle"
        @cancel="cancelBookmark"
        @confirm="saveBookmark"
      />
    </portal>
  </div>
</template>

<script>
import shortid from "shortid";
import store from "../store/";
import VSearchFilter from "../components/search-filter/search-filter.vue";
import VNotFound from "./not-found.vue";

import api from "../api";
import csv from "../helpers/csv";

export default {
  name: "items",
  metaInfo() {
    return {
      title: this.$helpers.formatTitle(this.collection)
    };
  },
  components: {
    VSearchFilter,
    VNotFound
  },
  data() {
    return {
      selection: [],
      meta: null,
      preferences: null,
      confirmRemove: false,

      bookmarkModal: false,
      bookmarkTitle: "",

      notFound: false
    };
  },
  computed: {
    activity() {
      return this.collection === "directus_activity";
    },
    breadcrumb() {
      if (this.collection === "directus_users") {
        return [
          {
            name: this.$t("user_directory"),
            path: "/users"
          }
        ];
      }

      if (this.collection === "directus_files") {
        return [
          {
            name: this.$t("file_library"),
            path: "/files"
          }
        ];
      }

      const breadcrumb = [];

      if (this.collection.startsWith("directus_")) {
        breadcrumb.push({
          name: this.$helpers.formatTitle(this.collection.substr(9)),
          path: `/${this.collection.substring(9)}`
        });
      } else {
        breadcrumb.push(
          {
            name: this.$t("collections"),
            path: "/collections"
          },
          {
            name: this.$t(`collections-${this.collection}`),
            path: `/collections/${this.collection}`
          }
        );
      }

      return breadcrumb;
    },
    fields() {
      const fields = this.$store.state.collections[this.collection].fields;
      return Object.values(fields).map(field => ({
        ...field,
        name: this.$helpers.formatTitle(field.field)
      }));
    },
    batchURL() {
      return `/collections/${this.collection}/${this.selection
        .map(item => item[this.primaryKeyField])
        .join(",")}`;
    },
    currentBookmark() {
      if (!this.preferences) return;

      const bookmarks = this.$store.state.bookmarks;
      const preferences = {
        collection: this.preferences.collection,
        search_query: this.preferences.search_query,
        filters: this.preferences.filters,
        view_options: this.preferences.view_options,
        view_type: this.preferences.view_type,
        view_query: this.preferences.view_query
      };
      const currentBookmark = bookmarks.filter(bookmark => {
        const bookmarkPreferences = {
          collection: bookmark.collection,
          search_query: bookmark.search_query,
          filters: bookmark.filters,
          view_options: bookmark.view_options,
          view_type: bookmark.view_type,
          view_query: bookmark.view_query
        };
        return this.$lodash.isEqual(bookmarkPreferences, preferences);
      })[0];
      return currentBookmark || null;
    },
    collection() {
      return this.$route.params.collection;
    },
    collectionInfo() {
      return this.$store.state.collections[this.collection];
    },
    emptyCollection() {
      return (this.meta && this.meta.total_count === 0) || false;
    },
    filters() {
      if (!this.preferences) return [];
      return this.preferences.filters || [];
    },
    searchQuery() {
      if (!this.preferences) return "";
      return this.preferences.search_query || "";
    },
    viewType() {
      if (!this.preferences) return "tabular";
      return this.preferences.view_type || "tabular";
    },
    viewQuery() {
      if (!this.preferences) return {};

      const viewQuery =
        (this.preferences.view_query &&
          this.preferences.view_query[this.viewType]) ||
        {};

      // Filter out the fieldnames of fields that don't exist anymore
      // Sorting / querying fields that don't exist anymore will return
      // a 422 in the API and brick the app

      const collectionFieldNames = this.fields.map(f => f.field);

      if (viewQuery.fields) {
        viewQuery.fields = viewQuery.fields
          .split(",")
          .filter(fieldName => collectionFieldNames.includes(fieldName))
          .join(",");
      }

      if (viewQuery.sort) {
        // If the sort is descending, the fieldname starts with -
        // The fieldnames in the array of collection field names don't have this
        // which is why we have to take it out.
        const sortFieldName = viewQuery.sort.startsWith("-")
          ? viewQuery.sort.substring(1)
          : viewQuery.sort;

        if (collectionFieldNames.includes(sortFieldName) === false) {
          viewQuery.sort = this.primaryKeyField;
        }
      }

      return viewQuery;
    },
    viewOptions() {
      if (!this.preferences) return {};
      return (
        (this.preferences.view_options &&
          this.preferences.view_options[this.viewType]) ||
        {}
      );
    },
    resultCopy() {
      if (!this.meta || !this.preferences) return this.$t("loading");

      const isFiltering =
        !this.$lodash.isEmpty(this.preferences.filters) ||
        (!this.$lodash.isNil(this.preferences.search_query) &&
          this.preferences.search_query.length > 0);

      return isFiltering
        ? this.$tc("item_count_filter", this.meta.result_count, {
            count: this.$n(this.meta.result_count)
          })
        : this.$tc("item_count", this.meta.total_count, {
            count: this.$n(this.meta.total_count)
          });
    },
    fieldNames() {
      return this.fields.map(field => field.field);
    },
    layoutNames() {
      if (!this.$store.state.extensions.layouts) return {};
      const translatedNames = {};
      Object.keys(this.$store.state.extensions.layouts).forEach(id => {
        translatedNames[id] = this.$store.state.extensions.layouts[id].name;
      });
      return translatedNames;
    },
    statusField() {
      if (!this.fields) return null;

      return (
        this.$lodash.find(
          Object.values(this.fields),
          field => field.type.toLowerCase() === "status"
        ) || {}
      ).field;
    },

    // Get the status name of the value that's marked as soft delete
    // This will make the delete button update the item to the hidden status
    // instead of deleting it completely from the database
    softDeleteStatus() {
      if (!this.collectionInfo.status_mapping) return null;

      const statusKeys = Object.keys(this.collectionInfo.status_mapping);
      const index = this.$lodash.findIndex(
        Object.values(this.collectionInfo.status_mapping),
        { soft_delete: true }
      );
      return statusKeys[index];
    },

    userCreatedField() {
      if (!this.fields) return null;

      return (
        this.$lodash.find(
          Object.values(this.fields),
          field => field.type.toLowerCase() === "user_created"
        ) || {}
      ).field;
    },
    primaryKeyField() {
      if (!this.fields) return null;
      return this.$lodash.find(this.fields, { primary_key: true }).field;
    },
    permission() {
      return this.$store.state.permissions[this.collection];
    },
    addButton() {
      if (this.$store.state.currentUser.admin) return true;

      if (this.statusField) {
        return (
          Object.values(this.permission.statuses).some(
            permission => permission.create === "full"
          ) || this.permission.$create.create === "full"
        );
      }

      return this.permission.create === "full";
    },
    deleteButton() {
      if (!this.selection) return false;
      if (this.selection.length === 0) return false;
      return true;
    },
    deleteButtonEnabled() {
      if (this.$store.state.currentUser.admin) return true;
      const currentUserID = this.$store.state.currentUser.id;
      let enabled = true;

      this.selection.forEach(item => {
        const status = this.statusField ? item[this.statusField] : null;
        const permission = this.statusField
          ? this.permission.statuses[status]
          : this.permission;
        const userID = item[this.userCreatedField];

        if (permission.delete === "none") {
          return (enabled = false);
        }

        if (permission.delete === "mine" && userID !== currentUserID) {
          return (enabled = false);
        }

        if (permission.delete === "role") {
          const userRoles = this.$store.state.users[userID].roles;
          const currentUserRoles = this.$store.state.currentUser.roles;
          let contains = false;

          userRoles.forEach(role => {
            if (currentUserRoles.includes(role)) contains = true;
          });

          if (contains === false) return (enabled = false);
        }
      });

      return enabled;
    },
    editButton() {
      if (this.selection && this.selection.length > 1) return true;
      return false;
    },
    editButtonEnabled() {
      const currentUserID = this.$store.state.currentUser.id;
      let enabled = true;

      if (this.$store.state.currentUser.admin) return true;

      this.selection.forEach(item => {
        const status = this.statusField ? item[this.statusField] : null;
        const permission = this.statusField
          ? this.permission.statuses[status]
          : this.permission;
        const userID = item[this.userCreatedField];

        if (permission.update === "none") {
          return (enabled = false);
        }

        if (permission.update === "mine" && userID !== currentUserID) {
          return (enabled = false);
        }

        if (permission.update === "role") {
          const userRoles = this.$store.state.users[userID].roles;
          const currentUserRoles = this.$store.state.currentUser.roles;
          let contains = false;

          userRoles.forEach(role => {
            if (currentUserRoles.includes(role)) contains = true;
          });

          if (contains === false) return (enabled = false);
        }
      });

      return enabled;
    }
  },
  methods: {
    scaricaCSV(){
      let fields = this.$lodash.split(this.viewQuery.fields,',')
      let headers = this.$lodash.map(fields, field => {
        return {
          field: field,
          name: this.$lodash.chain(this.$refs.listing.fields).find(el => el.field === field).get("name").value()
        } 
      })
      this.$api
        .getItems(this.collection, {
          ...this.$refs.listing.formatParams(),
          limit:10000,
          offset:0
        })
        .then(res => {
          csv.download(headers, res.data, 'export')
        })
    },
    cancelBookmark() {
      this.bookmarkTitle = "";
      this.bookmarkModal = false;
    },
    setViewQuery(query) {
      const newViewQuery = {
        ...this.preferences.view_query,
        [this.viewType]: {
          ...this.viewQuery,
          ...query
        }
      };
      this.updatePreferences("view_query", newViewQuery);
    },
    setViewOptions(options) {
      const newViewOptions = {
        ...this.preferences.view_options,
        [this.viewType]: {
          ...this.viewOptions,
          ...options
        }
      };
      this.updatePreferences("view_options", newViewOptions);
    },
    updatePreferences(key, value, combine = false) {
      if (combine) {
        value = {
          ...this.preferences[key],
          ...value
        };
      }
      this.$set(this.preferences, key, value);

      // user vs role vs collection level preferences, == checks both null and undefined
      const isPreferenceFallback = this.preferences.user == null;
      if (isPreferenceFallback) {
        return this.createCollectionPreset();
      }

      const id = this.$helpers.shortid.generate();
      this.$store.dispatch("loadingStart", { id });

      return this.$api
        .updateCollectionPreset(this.preferences.id, {
          [key]: value
        })
        .then(() => {
          this.$store.dispatch("loadingFinished", id);
        })
        .catch(error => {
          this.$store.dispatch("loadingFinished", id);
          this.$events.emit("error", {
            notify: this.$t("something_went_wrong_body"),
            error
          });
        });
    },
    createCollectionPreset() {
      const id = this.$helpers.shortid.generate();
      this.$store.dispatch("loadingStart", { id });

      const preferences = { ...this.preferences };
      delete preferences.id;

      return this.$api
        .createCollectionPreset({
          ...preferences,
          collection: this.collection,
          user: this.$store.state.currentUser.id
        })
        .then(({ data }) => {
          this.$store.dispatch("loadingFinished", id);
          this.$set(this.preferences, "id", data.id);
          this.$set(this.preferences, "user", data.user);
        })
        .catch(error => {
          this.$store.dispatch("loadingFinished", id);
          this.$events.emit("error", {
            notify: this.$t("something_went_wrong_body"),
            error
          });
        });
    },
    clearFilters() {
      this.updatePreferences("filters", null);
      this.updatePreferences("search_query", null);
    },
    remove() {
      const id = this.$helpers.shortid.generate();
      this.$store.dispatch("loadingStart", { id });

      let request;

      const itemKeys = this.selection.map(item => item[this.primaryKeyField]);

      if (this.softDeleteStatus) {
        request = this.$api.updateItem(this.collection, itemKeys.join(","), {
          [this.statusField]: this.softDeleteStatus
        });
      } else {
        request = this.$api.deleteItems(
          this.collection,
          this.selection.map(item => item[this.primaryKeyField])
        );
      }

      request
        .then(() => {
          this.$store.dispatch("loadingFinished", id);
          this.$refs.listing.getItems();
          this.selection = [];
          this.confirmRemove = false;
        })
        .catch(error => {
          this.$store.dispatch("loadingFinished", id);
          this.$events.emit("error", {
            notify: this.$t("something_went_wrong_body"),
            error
          });
        });
    },
    saveBookmark() {
      const preferences = { ...this.preferences };
      preferences.user = this.$store.state.currentUser.id;
      preferences.title = this.bookmarkTitle;
      delete preferences.id;
      delete preferences.role;
      if (!preferences.collection) {
        preferences.collection = this.collection;
      }
      const id = this.$helpers.shortid.generate();
      this.$store.dispatch("loadingStart", { id });

      this.$store
        .dispatch("saveBookmark", preferences)
        .then(() => {
          this.$store.dispatch("loadingFinished", id);
          this.bookmarkModal = false;
          this.bookmarkTitle = "";
        })
        .catch(error => {
          this.$store.dispatch("loadingFinished", id);
          this.$events.emit("error", {
            notify: this.$t("something_went_wrong_body"),
            error
          });
        });
    }
  },
  watch: {
    $route() {
      if (this.$route.query.b) {
        this.$router.replace({
          path: this.$route.path
        });
      }
    }
  },
  beforeRouteEnter(to, from, next) {
    const { collection } = to.params;

    const collectionInfo = store.state.collections[collection] || null;

    if (
      collection.startsWith("directus_") === false &&
      collectionInfo === null
    ) {
      return next(vm => (vm.notFound = true));
    }

    if (collectionInfo && collectionInfo.single) {
      return next(`/collections/${collection}/1`);
    }

    const id = shortid.generate();
    store.dispatch("loadingStart", { id });

    return api
      .getMyListingPreferences(collection)
      .then(preferences => {
        store.dispatch("loadingFinished", id);
        next(vm => {
          vm.$data.preferences = preferences;
        });
      })
      .catch(error => {
        store.dispatch("loadingFinished", id);
        this.$events.emit("error", {
          notify: this.$t("something_went_wrong_body"),
          error
        });
      });
  },
  beforeRouteUpdate(to, from, next) {
    const { collection } = to.params;

    this.preferences = null;
    this.selection = [];
    this.meta = {};
    this.notFound = false;

    const collectionInfo = this.$store.state.collections[collection] || null;

    if (
      collection.startsWith("directus_") === false &&
      collectionInfo === null
    ) {
      this.notFound = true;
      return next();
    }

    if (collectionInfo && collectionInfo.single) {
      return next(`/collections/${collection}/1`);
    }

    const id = this.$helpers.shortid.generate();
    this.$store.dispatch("loadingStart", { id });

    return api
      .getMyListingPreferences(collection)
      .then(preferences => {
        this.$store.dispatch("loadingFinished", id);
        this.preferences = preferences;
        next();
      })
      .catch(error => {
        this.$store.dispatch("loadingFinished", id);
        this.$events.emit("error", {
          notify: this.$t("something_went_wrong_body"),
          error
        });
      });
  }
};
</script>

<style lang="scss" scoped>
label.style-4 {
  padding-bottom: 5px;
}
.bookmark {
  margin-left: 10px;
  opacity: 0.4;
  transition: opacity var(--fast) var(--transition);
  position: relative;
  &:hover {
    opacity: 1;
  }
  i {
    font-size: 24px;
    height: 20px;
    transform: translateY(-3px); // Vertical alignment of icon
  }
}
.bookmark.active {
  opacity: 1;
  i {
    color: var(--accent);
  }
}
.bookmark-name {
  color: var(--accent);
  margin-left: 5px;
  margin-top: 3px;
  font-size: 0.67em;
  line-height: 1.1;
  font-weight: 700;
  text-transform: uppercase;
}
button.csv{
  font-size: 14px;
  background-color: var(--darker-gray);
  width: 100%;
  display: block;
  color: var(--white);
  padding: 10px 0;
  margin:0 0 20px 0;
  border-radius: var(--border-radius);
  transition: background-color var(--fast) var(--transition);
}
</style>
